<template>
  <ConfirmDialog group="warning" ref="dialog">
    <template #container="{ message, acceptCallback, rejectCallback }">
      <div class="flex flex-col items-center p-5 bg-surface-0 dark:bg-surface-700 rounded-md gap-4">

        <!-- Icon -->
        <div
            class="rounded-full bg-orange-400 dark:bg-orange-400 text-surface-0 dark:text-surface-900 inline-flex justify-center items-center h-[6rem] w-[6rem] -mt-[3rem]">
          <IconAlertTriangleFilled class="h-10 w-10 text-surface-50" aria-hidden="true"/>
        </div>

        <!-- Header -->
        <span class="font-bold text-2xl block text-surface-900 dark:text-surface-0 w-full">{{
            message.header
          }}</span>

        <!-- Message -->
        <p class="text-surface-800 dark:text-surface-100">{{ message.message }}</p>

        <!-- Content -->
        <Fieldset
            :legend="$t('pages.manufacturers.dialogs.save_approved_firmware_dialog.regulation_changes')"
            class="flex-1 flex grow flex-col w-full"
        >
          <div v-if="previousFirmware?.version_number" class="relative">
            <div class="flex gap-1 mb-2">
              <span class="font-semibold text-surface-700">
                {{ $t('pages.manufacturers.dialogs.save_approved_firmware_dialog.previous') }} -
              </span>
              <h3 class="flex-1">{{ previousFirmware?.version_number ?? '-' }}</h3>
              <div v-if="previousFirmware.release_notes.length > 0"
                   class="absolute right-0 top-0">
                <Button
                    type="button"
                    @click="togglePreviousReleaseNotesMenu($event)"
                    severity="secondary"
                    size="small"
                    text
                    aria-haspopup="true"
                    aria-controls="overlay_menu"
                >
                  <template #icon>
                    <IconDots/>
                  </template>
                </Button>
                <Menu
                    ref="previousReleaseNotesMenu"
                    id="overlay_menu"
                    :model="getMenuItems(previousFirmware)"
                    :popup="true"
                >
                </Menu>
              </div>
            </div>
            <div class="flex items-center gap-2 flex-wrap">
              <Tag
                  v-if="previousFirmware?.regulations.length > 0"
                  v-for="regulation in previousFirmware.regulations"
                  severity="secondary"
                  :value="regulation.name"
              />
              <span v-else class="text-sm font-medium text-slate-600 flex flex-row">
                {{ $t('pages.manufacturers.dialogs.save_approved_firmware_dialog.no_regulations') }}
              </span>
            </div>
          </div>

          <Divider/>

          <div v-if="newFirmware?.version_number" class="relative">
            <div class="flex gap-1 mb-2">
                <span class="font-semibold text-surface-700">
                  {{ $t('pages.manufacturers.dialogs.save_approved_firmware_dialog.new') }} -
                </span>
                <h3 class="flex-1">{{ newFirmware?.version_number ?? '-' }}</h3>
                <div v-if="newFirmware.release_notes.length > 0"
                     class="absolute right-0 top-0">
                  <Button
                      type="button"
                      @click="toggleNewReleaseNotesMenu($event)"
                      severity="secondary"
                      size="small"
                      text
                      aria-haspopup="true"
                      aria-controls="overlay_menu"
                  >
                    <template #icon>
                      <IconDots/>
                    </template>
                  </Button>
                  <Menu
                      ref="newReleaseNotesMenu"
                      id="overlay_menu"
                      :model="getMenuItems(newFirmware)"
                      :popup="true">
                  </Menu>
                </div>
            </div>
            <div class="flex items-center gap-2 flex-wrap">
              <Tag v-if="newFirmware?.regulations.length > 0" v-for="regulation in stylisedNewRegulations"
                   :severity="regulation.new ? 'warning' : 'secondary'">
                <span class="font-bold" v-if="regulation.new">+ {{ regulation.name }}</span>
                <span class="font-bold" v-if="!regulation.new">{{ regulation.name }}</span>
              </Tag>
              <span v-else class="text-sm font-medium text-slate-600 flex flex-row">
                  {{ $t('pages.manufacturers.dialogs.save_approved_firmware_dialog.no_regulations') }}
                </span>
            </div>
          </div>
        </Fieldset>

        <!-- Footer -->
        <div class="w-full flex flex-row items-stretch justify-end gap-4 mt-6">
          <Button :label="message.rejectLabel ?? 'Cancel'" severity="secondary" size="large" outlined
                  @click="rejectCallback" class="w-[8rem]"></Button>
          <Button :label="message.acceptLabel ?? 'Confirm'" severity="warning" size="large" @click="acceptCallback"
                  class="w-[8rem]"></Button>
        </div>
      </div>
    </template>
  </ConfirmDialog>
</template>

<script>
import {IconAlertTriangleFilled, IconDots} from "@tabler/icons-vue";
import ConfirmDialog from "primevue/confirmdialog";
import {useDownloadLink} from "@/Composables/DownloadLink.js";
import {mapStores} from "pinia";
import {useFirmwareReleaseNotesStore} from "@/stores/firmwareReleaseNote.js";
import {useFirmwaresStore} from "@/stores/firmwares.js";

export default {
  name: "SaveApprovedFirmwareDialog",
  components: {
    ConfirmDialog,
    IconAlertTriangleFilled,
    IconDots
  },
  props: {
    data: {
      data: Object,
      required: true,
    },
  },
  data() {
    return {
      previousFirmware: undefined,
      newFirmware: undefined,
      firmwareParams: {
        include: [
          'regulations',
          'releaseNotes',
        ]
      },
    }
  },
  computed: {
    ...mapStores(useFirmwareReleaseNotesStore),
    ...mapStores(useFirmwaresStore),
    stylisedNewRegulations() {
      return this.newFirmware.regulations.map(regulation => {
        regulation.new = !this.previousFirmware.regulations.find(r => r.id === regulation.id);
        return regulation;
      });
    },
  },
  methods: {
    togglePreviousReleaseNotesMenu(e) {
      this.$refs.previousReleaseNotesMenu.toggle(e);
    },
    toggleNewReleaseNotesMenu(e) {
      this.$refs.newReleaseNotesMenu.toggle(e);
    },
    async downloadFirmwareReleaseNote(firmwareReleaseNote) {
      if (firmwareReleaseNote) {
        try {
          const response = await this.firmwareReleaseNotesStore.showFirmwareReleaseNote(firmwareReleaseNote.id, {download: 1});
          useDownloadLink(response.url);
        } catch (error) {
          console.log('Error downloading firmware release note', error);
        }
      }
    },
    getMenuItems(firmware) {
      let menuItems = [{
        label : 'Release Notes',
        items: []
      }];

      firmware?.release_notes?.forEach(release_note => {
        menuItems[0].items.push({
          label: release_note.name, icon: 'IconFileInfo', command: (e) => {
            this.downloadFirmwareReleaseNote(release_note)
          }
        })
      });

      return menuItems;
    },
  },
  watch: {
    'data': {
      handler: async function (newValue, oldValue) {
        if (this.data.previous && this.data.new) {
          this.previousFirmware = undefined;
          this.newFirmware = undefined;
          this.previousFirmware = await this.firmwaresStore.showFirmware(this.data.previous, this.firmwareParams);
          this.newFirmware = await this.firmwaresStore.showFirmware(this.data.new, this.firmwareParams);
        }
      },
      immediate: true,
    },
  },
}
</script>
